import {COMPANY_CODES, COMPANY_WITHOUT_VAT_NR, DENOMINATOR, LANGUAGES, LOCALES, regexList, RENTAL_TYPES, SEGMENTS} from "./config";
import dayjs from "dayjs";
import {CustomerSearchFilters, NetworkScope} from './types';
import {ICustomer, ICustomerData, IPerson, IRaAncillary} from "./models/models";
import {base64StringToBlob} from "blob-util";
import {PAYMENT_TIMING} from "./create-ra/customer-data/config";
import Resizer from "react-image-file-resizer";
import {getStandardResolutionByName} from "../../util/state/commonFunctions";
import {RecoveryScope} from "../recovery/types";

/**
 * Deep clone value function (copy 1st level values)
 */
export const deepCloneValue = (value: any): any => {
    const type = typeof value;
    switch (type) {
        case 'object':
            if (Array.isArray(value)) {
                return value.concat([]);
            } else {
                let clone = {} as any;
                for (const prop in value) {
                    const propType = typeof value[prop];
                    switch (propType) {
                        case 'object':
                            if (Array.isArray(value[prop])) {
                                clone[prop] = value[prop as any].concat([]);
                                // clone[prop] = value[prop as any].map((elem: any) => deepCloneValue(elem));   // todo test
                            } else {
                                clone[prop] = Object.assign({}, value[prop]);
                                // clone[prop] = deepCloneValue(value[prop]);                                   // todo test
                            }
                            break;
                        default:
                            clone[prop] = value[prop];
                    }
                }
                return clone;
            }
        default:
            return value;
    }
}

/**
 * Format Currency
 */
export const formatCurrency = (locale = LOCALES.it, amountInCent: number | null, currency: string = 'EUR'): string => {
    if(amountInCent === undefined || amountInCent === null){
        return "";
    }

    return new Intl.NumberFormat(locale, {
           style: 'currency',
           currency: currency
           }).format(amountInCent / 100);
};

/**
 * Resize img
 * @param file
 * @return {file} resized file if it had the type image
 */
export const resizeImg = async (file: File) => {
    if (!!file?.type?.startsWith('image')) {
        // @ts-ignore
        const maxResolution = getStandardResolutionByName(window.appConfig.MAX_RESOLUTION_FOR_IMG_RESIZE);
        // @ts-ignore
        const minResolution = getStandardResolutionByName(window.appConfig.MIN_RESOLUTION_FOR_IMG_RESIZE);
        // @ts-ignore
        const quality = window.appConfig.QUALITY_FOR_IMG_RESIZE || 100;

        return await new Promise((resolve) => {
            Resizer.imageFileResizer(
                file,
                maxResolution?.width,
                maxResolution?.height,
                "JPEG",
                quality,
                0,
                (uri: any) => {
                    resolve(uri);
                },
                "file",
                minResolution?.width,
                minResolution?.height
            );
        });
    } else {
        return file;
    }
}

/**
 * Get Locale value by language
 */
export const getLocaleByLang = (lang: string): 'it-IT' | 'en-IN' | 'fr-FR' => {
    let prop: 'it-IT' | 'en-IN' | 'fr-FR' = 'it-IT';
    LANGUAGES.every((elem) => {
        if (lang.includes(elem)) {
            prop = LOCALES[elem as 'it' | 'en' | 'fr'];
            return false;
        }
        return true;
    });

    return prop;
}

/**
 * Check ancillaryUnits/ancillaryMileage
 */
export const checkAncillaryUnitsMileage = (ancillaries: IRaAncillary[]) => {
    if (ancillaries || Array.isArray(ancillaries)) {
        return ancillaries
                .map((ancillary: any) => {
                    if (!ancillary.ancillaryUnits) {
                        ancillary.ancillaryUnits = parseInt(ancillary.ancillaryMileage);
                    }
                    return ancillary;
                });
    }
    return [];
}

/**
 * Get dirty fields values
 */
export const getFormDirtyValues = (dirtyFields: any, values: any): any => {
    return Object.fromEntries(
        Object.keys(dirtyFields)
            .map((key) => [key, values[key]])
    );
};

/**
 * Compose string with numerator/denominator
 * @param  numerator {number}
 * @return {string}
 */
export const composeFraction = (numerator: number): string => {
    if(numerator !== null){
        return `${numerator}/${DENOMINATOR}`;
    } else {
        return '';
    }
}

/**
 * Compose string with %
 * @param  percentage {number}
 * @return {string}
 */
export const composePercentage = (percentage: number): string => {
    if(percentage != null){
        return `${percentage}%`;
    } else {
        return '';
    }
}

/**
 * Compose DateTimeStamp
 */
export const dayjsDateTimeStamp = () => dayjs().set('second', 0).set('millisecond', 0);

export const hasScope = (globalData: any, scope: NetworkScope | RecoveryScope) => {
    return (
        globalData.user.profiles
            .some((p: any) => (
                p.auths
                    .some((a: any) => a.name === scope)
            ))
    )
}

/**
 * Save file on 'Download' folder
 * @param data
 * @param fileName
 */
export const saveFile = (data: any, fileName: string) => {
    const blob = new Blob([data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
}

/**
 * Get element from selected google api response
 * @param {any[]} apiResponse
 * @param {string[]} filters
 * @return string[]
 */
export const getDataFromApiGoogle = (apiResponse: any[], filters: string[]): any[]  => {
    return filters?.map((filter: string) => apiResponse?.find((elem: any) => elem.types.includes(filter)));
}

export const fillPersonResponse = (driver: IPerson): ICustomerData => {
    return {
        name: driver.firstName,
        surname: driver.lastName,
        gender: !!driver.gender ? driver.gender : null,
        birthDate: !!driver.birthDate ? dayjs(driver.birthDate) : null,
        birthNation: !!driver.birthNation?.code ? driver.birthNation : null,
        // birthPlace: !!driver.birthPlace ? driver.birthPlace : null,
        birthPlace: !!driver.birthPlace?.code ? driver.birthPlace.code : '',
        residenceCountry: !!driver.countryResidence?.code ? driver.countryResidence : null,
        // residenceCity: !!driver.cityResidence ? driver.cityResidence : null,
        residenceCity: !!driver.cityResidence?.code ? driver.cityResidence.code : '',
        residenceProvince: !!driver.provinceResidence ? {
            codeProvince: driver.provinceResidence.code,
            description: driver.provinceResidence.description ?? ''
        } : null,
        certifiedEmail: driver.certifiedEmail ?? '',
        addressGoogleSearch: '',
        address: driver.address ?? '',
        streetNumber: driver.streetNumber ?? '',
        postcode: driver.postCode ?? '',
        mobile: driver.mobile ?? '',
        prefix: !!driver.prefix?.code ? driver.prefix : null,
        email: driver.email,
        fiscalCode: driver.fiscalCode ?? '',
        customerType: driver.customerType ?? '',
        customerID: !!driver.customerId ? driver.customerId : '',
        cbId: !!driver.cbId ? driver.cbId : '',
        uuid: '',
        citizenship: driver.citizenship ?? null,
        drivingLicenseNumber: driver.drivingLicenseNumber ?? '',
        drivingLicenseCategory: driver.drivingLicenseCategory ?? '',
        issuingAuthority: driver.drivingLicenseIssuingAuthority ?? '',
        idIssuingCountry: null,
        idIssuingProvince: null,
        issuingDate: !!driver.drivingLicenseIssueDate ? dayjs(driver.drivingLicenseIssueDate, 'YYYY-MM-DD') : null,
        expireDate: !!driver.drivingLicenseExpiryDate ? dayjs(driver.drivingLicenseExpiryDate, 'YYYY-MM-DD') : null,
        drivingLicenseIssuingCountry: null,
        drivingLicenseIssuingProvince: null,
        drivingLicenseIssuingProvinceName: driver.drivingIssuingProvince ?? null,
        issuingPlace: driver.drivingLicenseIssuingPlace ?? null,
        drivingLicenseFirstReleaseDate: driver.drivingLicenseFirstReleaseDate ? dayjs(driver.drivingLicenseFirstReleaseDate, 'YYYY-MM-DD') : null,
        // attachmentId
        drivingLicenseDocCode: driver.drivingLicenseDocFront?.attachmentId ?? null,
        dossierIdentifier: !!driver?.idDocFront?.dossierIdentifier ? driver.idDocFront.dossierIdentifier : (!!driver.drivingLicenseDocFront?.dossierIdentifier ? driver.drivingLicenseDocFront.dossierIdentifier : ''),
        drivingLicenseDocFront: driver.drivingLicenseDocFront?.documentFile ? base64StringToBlob(driver.drivingLicenseDocFront.documentFile) as File : null,
        drivingLicenseDocBack: driver.drivingLicenseDocBack?.documentFile ? base64StringToBlob(driver.drivingLicenseDocBack.documentFile) as File : null,
        idNumber: driver.idNumber ?? '',
        idType: driver.idType ?? '',
        idIssueDate: driver.idIssueDate ? dayjs(driver.idIssueDate, 'YYYY-MM-DD') : null,
        idExpiryDate: driver.idExpiryDate ? dayjs(driver.idExpiryDate, 'YYYY-MM-DD') : null,
        idIssuingAuthority: driver.idIssuingAuthority ?? '',
        idIssuingProvinceName: driver.idIssuingProvince ?? null,
        idIssuingPlace: driver.idIssuingPlace ?? null,
        idDocCode: driver.idDocFront?.attachmentId ?? null,
        idDocFront: driver.idDocFront?.documentFile ? base64StringToBlob(driver.idDocFront.documentFile) as File : null,
        idDocBack: driver.idDocBack?.documentFile ? base64StringToBlob(driver.idDocBack.documentFile) as File : null,
        verifiedDocuments: driver.verifiedDocuments == undefined ? null : driver.verifiedDocuments,
        drivingLicenseDocTitleFront: driver.drivingLicenseDocFront?.documentTitle || '',
        drivingLicenseDocPathFront: driver.drivingLicenseDocFront?.path || '',
        drivingLicenseDocTitleBack: driver.drivingLicenseDocBack?.documentTitle || '',
        drivingLicenseDocPathBack: driver.drivingLicenseDocBack?.path || '',
        idDocTitleFront: driver.idDocFront?.documentTitle || '',
        idDocPathFront: driver.idDocFront?.path || '',
        idDocTitleBack: driver.idDocBack?.documentTitle || '',
        idDocPathBack: driver.idDocBack?.path || ''
    }
}

export const fillDriverResponse = (customer: ICustomer): ICustomerData => {
    return({
        name: customer.firstName,
        surname: customer.lastName,
        gender: customer.gender,
        birthDate: customer.birthDate ? dayjs(customer.birthDate) : null,
        birthNation: customer.birthNation ? customer.birthNation : null,
        birthPlace: customer.birthPlace?.code ? customer.birthPlace.code : '',
        residenceCountry: customer.countryResidence?.code ? customer.countryResidence : null,
        residenceCity: customer.residenceCity?.code ? customer.residenceCity.code : '',
        residenceProvince: customer.residenceProvince ? { codeProvince: customer.residenceProvince.code, description: customer.residenceProvince.description ?? ''} : null,
        address: customer.residenceStreetName,
        streetNumber: customer.residenceStreetNumber,
        postcode: customer.residencePostalCode,
        mobile: customer.mobile,
        prefix: customer.mobilePrefix,
        email: customer.email,
        certifiedEmail: customer.certifiedEmail,
        fiscalCode: customer.fiscalCode,
        customerType: customer.customerType,
        customerID: customer.customerId ? customer.customerId : '',
        cbId: customer.cbId ? customer.cbId : '',
        uuid: customer.uuid ?? '',
        citizenship: customer.citizenship ?? null,
        drivingLicenseNumber: customer.drivingLicenseNumber ?? '',
        drivingLicenseCategory: customer.drivingLicenseCategory,
        issuingAuthority: customer.drivingLicenseIssuingAuthority ?? '',
        issuingDate: customer.drivingLicenseIssueDate ? dayjs(customer.drivingLicenseIssueDate) : null,
        expireDate: customer.drivingLicenseExpiryDate ? dayjs(customer.drivingLicenseExpiryDate) : null,
        issuingPlace: customer.drivingLicenseIssuingPlace?.code ? customer.drivingLicenseIssuingPlace : null,
        drivingLicenseFirstReleaseDate: customer.drivingLicenseFirstReleaseDate ? dayjs(customer.drivingLicenseFirstReleaseDate) : null,
        drivingLicenseDocCode: customer.drivingLicenseDocFront?.attachmentId ?? null,
        drivingLicenseDocFront: customer.drivingLicenseDocFront?.documentFile ? base64StringToBlob(customer.drivingLicenseDocFront.documentFile) as File : null,
        dossierIdentifier: !!customer?.idDocFront?.dossierIdentifier ? customer.idDocFront.dossierIdentifier : (!!customer?.drivingLicenseDocFront?.dossierIdentifier ? customer.drivingLicenseDocFront.dossierIdentifier : ''),
        drivingLicenseDocBack: customer.drivingLicenseDocBack?.documentFile ? base64StringToBlob(customer.drivingLicenseDocBack.documentFile) as File : null,
        idNumber: customer.idNumber ?? '',
        idType: customer.idType ?? '',
        idIssueDate: customer.idIssueDate ? dayjs(customer.idIssueDate) : null,
        idExpiryDate: customer.idExpiryDate ? dayjs(customer.idExpiryDate) : null,
        idIssuingAuthority: customer.idIssuingAuthority ?? '',
        idIssuingPlace: customer.idIssuingPlace?.code ? customer.idIssuingPlace : null,
        idDocCode: customer.idDocFront?.attachmentId ?? null,
        idDocFront: customer.idDocFront?.documentFile ? base64StringToBlob(customer.idDocFront.documentFile) as File: null,
        idDocBack: customer.idDocBack?.documentFile ? base64StringToBlob(customer.idDocBack.documentFile) as File: null,
        drivingLicenseIssuingCountry: null,
        drivingLicenseIssuingProvince: null,
        drivingLicenseIssuingProvinceName: customer.drivingIssuingProvince ?? null,
        addressGoogleSearch: '',
        idIssuingCountry: null,
        idIssuingProvince: null,
        idIssuingProvinceName: customer.idIssuingProvince ?? null,
        verifiedDocuments: customer.verifiedDocuments == undefined ? null : customer.verifiedDocuments,
        drivingLicenseDocTitleFront: customer.drivingLicenseDocFront?.documentTitle || '',
        drivingLicenseDocPathFront: customer.drivingLicenseDocFront?.path || '',
        drivingLicenseDocTitleBack: customer.drivingLicenseDocBack?.documentTitle || '',
        drivingLicenseDocPathBack: customer.drivingLicenseDocBack?.path || '',
        idDocTitleFront: customer.idDocFront?.documentTitle || '',
        idDocPathFront: customer.idDocFront?.path || '',
        idDocTitleBack: customer.idDocBack?.documentTitle || '',
        idDocPathBack: customer.idDocBack?.path || ''
    })

}

export const validateEmail = (email: string): boolean => 
    regexList.email.test(email);

export const validateITFiscalCodePerson = (fiscalCode: string, residenceCountry: string | null | undefined): boolean =>{
    if(residenceCountry === 'IT') {
        return regexList.fiscalCode.test(fiscalCode);
    }
    return fiscalCode?.length >= 5
}

export const validateITFiscalCodeCompany = (vatNr: string, vatNrCountry: string | null | undefined): boolean => {
    if(vatNrCountry === 'IT') {
        return regexList.vatNumber.test(vatNr);
    }
    return vatNr?.length >= 5;
}

export const validateVatNr = (vatNr: string, vatNrCountry: string | null | undefined): boolean => {
    if(!!vatNrCountry) {
        return validateITFiscalCodeCompany(vatNr, vatNrCountry)
    }
    return false;
}

export const validateITFiscalCodeGeneric = (fiscalCode: string, country: string | null | undefined, legalForm: "company" | "vatNumber" | undefined) => {
    if(legalForm === 'vatNumber') {
        return validateITFiscalCodePerson(fiscalCode, country) || validateITFiscalCodeCompany(fiscalCode, country);
    }

    if(legalForm === 'company') {
        return validateITFiscalCodeCompany(fiscalCode, country)
    }

    return validateITFiscalCodePerson(fiscalCode, country)
}

export const validateDriverLicense = (driverLicense: string, country: string | null | undefined): boolean => {
    if(country === 'IT') {
        return  regexList.driverLicense.test(driverLicense);
    }
    return driverLicense.length >= 3;
}

export type TGenericValidateType  = keyof CustomerSearchFilters;
export const genericValidate = (type: TGenericValidateType, val: string, country?: string | undefined, legalForm?: "company" | "vatNumber" | undefined): boolean => {
    const validations = {
        email: (val: string) => validateEmail(val),
        taxCode: (val: string, country: string, legalForm?: "company" | "vatNumber" | undefined) => validateITFiscalCodeGeneric(val, country, legalForm),
        vatNumber: (val: string, country: string) => validateVatNr(val, country),
        drvLicenceNumber: (val: string, country: string) => validateDriverLicense(val, country),
    } as {[t: string]: (val: string, country?: string, legalForm?: "company" | "vatNumber" | undefined) => boolean}

    return validations[type](val, country || '', legalForm);
}

export const getRequiredVatNr = (legalEntity: COMPANY_CODES | '' | null | undefined): boolean => {
    if(!!legalEntity){
        return !COMPANY_WITHOUT_VAT_NR.includes(legalEntity);
    }
    return true;

}

export const isRetail = (rentalType: string) => rentalType === RENTAL_TYPES.RETAIL;
export const isImmutableRa = (segment: string, paymentTiming: string): boolean => paymentTiming === PAYMENT_TIMING.PREPAID || segment === SEGMENTS.SUBSCRIPTION

export const replaceObjectsInUrls = (url: string, obj: {[v: string]: any}): string => 
    Object.keys(obj).reduce((res: string, v: string) => res.replace(`{${v}}`, obj[v]), url)

export const filterObject = (obj: object, filter: (x: keyof object) => boolean): object =>{
    // @ts-ignore
    return Object.keys(obj).reduce((res, x) => filter(x) ? {...res, ...{[x]: obj[x]}} : res, {})
}

export const getSubstring = (str: string, start: string, end: string) => str.substring(str.indexOf(start)+start.length, str.indexOf(end));

export const commonPricingIntegrationErrorsManagement = (error: any): string | null => {
    let mess = null;
        switch (error.response?.data?.errorDetails) {
            //Ad ora si gestisce come errore generico
            /*case "\"ERROR_PRC_LOOP_DETECTED\"":
                mess = '';
                break;*/
            case "\"ERROR_PRC_UNASSOCIATED_PRODUCT_AND_LOCATION_GROUP\"":
                mess = 'common.error.pricingUnassociatedProductAndLocationGroup';
                break;
            case "\"ERROR_PRC_RATE_MISCONFIGURATION\"":
                mess = 'common.error.pricingRateMisconfiguration';
                break;
            case "\"ERROR_PRC_RATE_INCLUDED_KM_MISCONFIGURATION\"":
                mess = 'common.error.pricingRateIncludedKmMisconfiguration';
                break;
            case "\"ERROR_PRC_CALENDAR_BASED_RATE_MISCONFIGURATION\"":
                mess = 'common.error.pricingCalendarBasedRateMisconfiguration';
                break;
            case "\"ERROR_PRC_UPSELLING_RATE_MISCONFIGURATION\"":
                mess = 'common.error.pricingUpsellingRateMisconfiguration';
                break;
            case "\"ERROR_PRC_ANCILLARY_MISCONFIGURATION\"":
                mess = 'common.error.pricingAncillaryMisconfiguration';
                break;
            case "\"ERROR_PRC_ONE_WAY_ANCILLARY_MISCONFIGURATION\"":
                mess = 'common.error.pricingOneWayAncillaryMisconfiguration';
                break;
            case "\"ERROR_PRC_UNAVAILABLE_ANCILLARY_QUANTITY\"":
                mess = 'common.error.pricingUnavailableAncillaryQuantity';
                break;
            case "\"ERROR_PRC_UNAVAILABLE_ANCILLARY_FOR_PRE_RA_FILLING_PHASE\"":
                mess = 'common.error.pricingUnavailableAncillaryForPreRaFillingPhase';
                break;
            case "\"ERROR_PRC_EXCESSES_MISCONFIGURATION\"":
                mess = 'common.error.pricingExcessesMisconfiguration';
                break;
            case "\"ERROR_PRC_UNPERMITTED_PICKUP_AFTER_BOOKED_DO\"":
                mess = 'common.error.pricingUnpermittedPickupAfterBookedDropoff';
                break;
            case "\"ERROR_PRC_UNPERMITTED_OPERATION_BEFORE_PICKUP\"":
                mess = 'common.error.pricingUnpermittedOperationBeforePickup';
                break;
            case "\"ERROR_PRC_UNPERMITTED_RENTAL_EXTENSION\"":
                mess = 'common.error.pricingUmpermittedRentalExtension';
                break;
            case "\"ERROR_PRC_REQUIRED_RATE_CONFIGURATION_NOT_OFFERED\"":
            case "\"ERROR_PRC_REQUEST_NOT_COMPLIANT_OFFER\"":
                mess = 'common.error.pricingRequiredRateConfigurationNotOffered';
                break;
            case "\"ERROR_PRC_EXTRA_DAYS_REQUEST_FOR_CHECK_IN_BEFORE_ESTIMATED_DO\"":
                mess = 'common.error.pricingExtraDaysRequestForCheckInBeforeEstimatedDropoff';
                break;
            case "\"ERROR_PRC_MISSING_STANDARD_EXTRA_DAYS_RATE_DEFINITION\"":
                mess = 'common.error.pricingMissingStandardExtraDaysDefinition';
                break;
            case "\"ERROR_PRC_EXTRA_DAYS_RATE_MISCONFIGURATION\"":
                mess = 'common.error.pricingExtraDaysRateMisconfiguration';
                break;
            case "\"ERROR_PRC_UNPERMITTED_EARLY_PICKUP\"":
                mess = 'extensionManagement.message.error.errorUnpermittedEarlyPickup';
                break;
            case "\"ERROR_PRC_UNPERMITTED_LATE_PICKUP\"":
                mess = 'extensionManagement.message.error.errorUnpermittedLatePickup';
                break;
            case "\"ERROR_PRC_EXTENSION_OVER_360_DAYS\"":
                mess = 'extensionManagement.message.error.30DaysExtension';
                break;
        }

    return mess;
};